import { IGatsbyImageData } from "gatsby-plugin-image";
import React from "react";
import styled, { css } from "styled-components";
import { Container } from "@ui/Atoms";
import { DraggableCarousel } from "@ui/Molecules/DraggableCarousel";
import { rem } from "@ui/helpers";

import { StyledSection } from "../shared";

// Import local images
import priveraLogo from "./assets/privera.png";
import putzfrauLogo from "./assets/putzfrau.png";
import baloiseLogo from "./assets/baloise.png";
import wincasaLogo from "./assets/wincasa.png";
import migrosLogo from "./assets/migros.png";
import holmesLogo from "./assets/holmes.png";
import laurastarLogo from "./assets/laurastar.png";
import innosuisseLogo from "./assets/innosuisse.png";
import zurichMarathonLogo from "./assets/zurich-marathon.png";
import twentyKmLogo from "./assets/20km.png";
import ahvLogo from "./assets/ahv.png";
import silvesterlaufLogo from "./assets/silvesterlauf.png";
import zebraboxLogo from "./assets/zebrabox.png";

const partners: { href: string; img: string }[] = [
  {
    href: "https://www.privera.ch/en/home",
    img: priveraLogo,
  },
  {
    href: "https://www.putzfrau.ch/",
    img: putzfrauLogo,
  },
  {
    href: "https://www.baloise.com/en/home/news-stories/news/media-releases/2020/baloise-expands-its-home-ecosystem-with-equity-investment-in-batmaid.html",
    img: baloiseLogo,
  },
  {
    href: "https://www.wincasa.ch/en-us/home-en",
    img: wincasaLogo,
  },
  {
    href: "https://shop.migros.ch/",
    img: migrosLogo,
  },
  {
    href: "https://www.holmesplace.ch/en/",
    img: holmesLogo,
  },
  {
    href: "https://www.laurastar.ch/en",
    img: laurastarLogo,
  },
  {
    href: "https://www.innosuisse.ch/inno/en/home.html",
    img: innosuisseLogo,
  },
  {
    href: "https://www.zuerichmarathon.ch/home-en",
    img: zurichMarathonLogo,
  },
  {
    href: "https://20km.ch/",
    img: twentyKmLogo,
  },
  {
    href: "https://www.ahv-iv.ch/en/",
    img: ahvLogo,
  },
  {
    href: "https://www.silvesterlauf.ch/",
    img: silvesterlaufLogo,
  },
  {
    href: "https://www.zebrabox.ch/en/private/partnership-offers",
    img: zebraboxLogo,
  },
];

interface Partner {
  partner_logo: { gatsbyImageData: IGatsbyImageData; alt: string };
  partner_link: { url: string | null; target: string | null };
}

export interface PartnersProps {
  partners_title?: {
    text: string;
  };
  partners_logos: Partner[];
}

interface Props extends Omit<PartnersProps, "partners_logos"> {
  noPaddingBottom?: boolean;
  backgroundColor?: string;
  hasPartners: boolean;
}

/**
 * `partners_logos` is not used anymore to generate list of partners. Instead the `partners` array is used.
 */
const Partners = (props: Props): React.ReactElement | null => {
  if (!props.hasPartners) {
    return null;
  }

  return (
    <Section
      noPaddingBottom={props.noPaddingBottom}
      backgroundColor={props.backgroundColor}
      hasPartners={props.hasPartners}
    >
      <Container>
        <DraggableCarousel
          isVerticallyCentered
          whiteButtons
          slidesToShow={{
            mobile: 3,
            tablet: 5,
            desktop: 5,
          }}
          slidesAmount={partners.length}
        >
          {partners.map((partner, index) => {
            return (
              <ImageLink key={index} href={partner.href}>
                <StepContent>
                  <img src={partner.img} alt="company logo" />
                </StepContent>
              </ImageLink>
            );
          })}
        </DraggableCarousel>
      </Container>
    </Section>
  );
};

export { Partners };

const Section = styled(StyledSection)<{
  noPaddingBottom?: boolean;
  hasPartners: boolean;
}>`
  overflow: hidden;
  padding: ${props => props.theme.margins.base_x3};

  ${props =>
    props.noPaddingBottom &&
    css`
      padding-bottom: 0;
    `}

  /* additional hiding the section when is no partners */
  ${props =>
    !props.hasPartners &&
    css`
      display: none;
    `}
`;

const StepContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${rem(144)};

  img {
    max-height: ${rem(30)};
    /* stylelint-disable-next-line declaration-no-important -- GatsbyImage styling needs to be overwritten */
    margin: auto !important;
  }

  ${props => props.theme.breakpoints.desktop} {
    min-width: ${rem(180)};
    max-width: ${rem(180)};
  }
`;

const ImageLink = styled.a<{ href: string }>`
  ${props =>
    (props.href === "#" || props.href === "") &&
    css`
      pointer-events: none;
    `}
`;
